import type Anthropic from '@anthropic-ai/sdk';
import { withRetry } from './retry';
import { useUsage } from '../stores/useUsage';
import { makeAttemptSignal, makeStallWatchdog } from './abortHelpers';
import { llmMessages, llmStream } from '../lib/syncClient';

export type TextBlock = { type: 'text'; text: string; cache?: boolean };

export type GenerateOptions = {
  model: string;
  systemBlocks: TextBlock[];
  userText: string;
  maxTokens: number;
  signal?: AbortSignal;
  /** Per-attempt overall timeout, ms. Default 150s. */
  timeoutMs?: number;
  /** Label used for retry/timeout logging. */
  label?: string;
};

export type StreamCallbacks = {
  onChunk: (text: string) => void;
};

export type StreamGenerateOptions = GenerateOptions & {
  /** Per-attempt overall timeout for the whole stream, ms. Default 180s. */
  streamTimeoutMs?: number;
  /** Max gap between chunks before declaring the stream stalled, ms. Default 30s. */
  stallTimeoutMs?: number;
  /** If set, server persists the streamed text under this blob key. */
  persistAs?: string;
};

const DEFAULT_TIMEOUT_MS = 150_000;
const DEFAULT_STREAM_TIMEOUT_MS = 180_000;
const DEFAULT_STALL_TIMEOUT_MS = 30_000;

function buildSystem(blocks: TextBlock[]) {
  return blocks.map((b) =>
    b.cache
      ? {
          type: 'text' as const,
          text: b.text,
          cache_control: { type: 'ephemeral' as const },
        }
      : { type: 'text' as const, text: b.text }
  );
}

export async function generateText(opts: GenerateOptions): Promise<string> {
  const label = opts.label ?? 'generateText';
  return withRetry(
    async () => {
      const ctl = makeAttemptSignal(
        opts.signal,
        opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
        label
      );
      try {
        const resp = await llmMessages<Anthropic.Message>(
          {
            model: opts.model,
            max_tokens: opts.maxTokens,
            system: buildSystem(opts.systemBlocks),
            messages: [{ role: 'user', content: opts.userText }],
          },
          ctl.signal
        );
        try {
          useUsage.getState().recordAnthropic(opts.model, resp.usage);
        } catch (e) {
          console.warn('usage record failed', e);
        }
        const first = resp.content?.[0];
        if (first && first.type === 'text') return first.text;
        return '';
      } catch (e) {
        if (ctl.isOurAbort()) {
          throw new DOMException(
            (e as Error)?.message ?? `${label} timed out`,
            'TimeoutError'
          );
        }
        throw e;
      } finally {
        ctl.cleanup();
      }
    },
    { signal: opts.signal, label }
  );
}

export async function streamText(
  opts: StreamGenerateOptions,
  cb: StreamCallbacks
): Promise<string> {
  const label = opts.label ?? 'streamText';
  const overallMs = opts.streamTimeoutMs ?? DEFAULT_STREAM_TIMEOUT_MS;
  const stallMs = opts.stallTimeoutMs ?? DEFAULT_STALL_TIMEOUT_MS;
  return withRetry(
    async () => {
      const ctl = makeAttemptSignal(opts.signal, overallMs, label);
      const stall = makeStallWatchdog(stallMs, () =>
        ctl.abortWithTimeout(`${label} stalled — no chunks for ${stallMs}ms`)
      );
      try {
        let full = '';
        let usage: Anthropic.Usage | undefined;
        const body: Record<string, unknown> = {
          model: opts.model,
          max_tokens: opts.maxTokens,
          system: buildSystem(opts.systemBlocks),
          messages: [{ role: 'user', content: opts.userText }],
        };
        if (opts.persistAs) body.persist_as = opts.persistAs;
        stall.reset();
        for await (const event of llmStream(body, ctl.signal)) {
          stall.reset();
          if (event.type === '_error') {
            throw new Error((event.error as string) || 'stream error');
          }
          if (event.type === '_final') {
            const msg = event.message as { usage?: Anthropic.Usage } | undefined;
            usage = msg?.usage;
            continue;
          }
          if (event.type === 'content_block_delta') {
            const delta = (event as { delta?: { type?: string; text?: string } })
              .delta;
            if (delta?.type === 'text_delta' && typeof delta.text === 'string') {
              full += delta.text;
              cb.onChunk(delta.text);
            }
          }
        }
        stall.stop();
        if (usage) {
          try {
            useUsage.getState().recordAnthropic(opts.model, usage);
          } catch (e) {
            console.warn('usage record failed (stream)', e);
          }
        }
        return full;
      } catch (e) {
        if (ctl.isOurAbort()) {
          throw new DOMException(
            (e as Error)?.message ?? `${label} timed out`,
            'TimeoutError'
          );
        }
        throw e;
      } finally {
        stall.stop();
        ctl.cleanup();
      }
    },
    { signal: opts.signal, label }
  );
}
